/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/GatewayColumnRenderers.tsx
 🗓️ Created: 2025-01-17
 🧩 Description:
    Column renderers for Gateway DataGrid.
────────────────────────────────────────────────────────────────*/

import { Edit, Copy, Trash2, RefreshCw, Square, Play } from 'lucide-react';
import { Gateway } from '../types';
import { EMPTY_PLACEHOLDER, formatDate, formatBoolean } from '../utils/formatters';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';

const ACTION_BUTTON_STYLES = {
  edit: 'p-1 rounded transition-colors text-blue-600 hover:text-blue-700 hover:bg-blue-100/60 dark:text-blue-300 dark:hover:text-blue-200 dark:hover:bg-blue-900/30',
  duplicate: 'p-1 rounded transition-colors text-green-600 hover:text-green-700 hover:bg-green-100/60 dark:text-green-300 dark:hover:text-green-200 dark:hover:bg-green-900/30',
  delete: 'p-1 rounded transition-colors text-red-600 hover:text-red-700 hover:bg-red-100/60 dark:text-red-300 dark:hover:text-red-200 dark:hover:bg-red-900/30',
  reload: 'p-1 rounded transition-colors text-purple-600 hover:text-purple-700 hover:bg-purple-100/60 dark:text-purple-300 dark:hover:text-purple-200 dark:hover:bg-purple-900/30',
  stop: 'p-1 rounded transition-colors text-orange-600 hover:text-orange-700 hover:bg-orange-100/60 dark:text-orange-300 dark:hover:text-orange-200 dark:hover:bg-orange-900/30',
  start: 'p-1 rounded transition-colors text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100/60 dark:text-emerald-300 dark:hover:text-emerald-200 dark:hover:bg-emerald-900/30'
} as const;

/**
 * Render name column with description
 */
export function renderNameColumn(_value: unknown, item: Gateway, t: (key: string) => string) {
  return (
    <div>
      <div className="font-medium text-foreground">{item.name}</div>
      {item.description && (
        <div className="text-sm text-muted-foreground">{item.description}</div>
      )}
    </div>
  );
}

/**
 * Render username column
 */
export function renderUsernameColumn(_value: unknown, item: Gateway) {
  return (
    <span className="text-sm text-foreground">{item.username || EMPTY_PLACEHOLDER}</span>
  );
}

/**
 * Render proxy column
 */
export function renderProxyColumn(_value: unknown, item: Gateway) {
  return (
    <span className="text-sm text-foreground break-words">{item.proxy || EMPTY_PLACEHOLDER}</span>
  );
}

/**
 * Render register column
 */
export function renderRegisterColumn(_value: unknown, item: Gateway, t: (key: string) => string) {
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
      item.register
        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
        : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
    }`}>
      {formatBoolean(item.register, t)}
    </span>
  );
}

/**
 * Render enabled column
 */
export function renderEnabledColumn(_value: unknown, item: Gateway, t: (key: string) => string) {
  return item.enabled
    ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
        {t('common.enabled')}
      </span>
    )
    : (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
        {t('common.disabled')}
      </span>
    );
}

/**
 * Render description column
 */
export function renderDescriptionColumn(_value: unknown, item: Gateway) {
  return (
    <span className="text-sm text-muted-foreground">
      {item.description?.trim() || EMPTY_PLACEHOLDER}
    </span>
  );
}

/**
 * Render createdAt column
 */
export function renderCreatedAtColumn(_value: unknown, item: Gateway) {
  return (
    <span className="text-sm text-muted-foreground">
      {formatDate(item.createdAt)}
    </span>
  );
}

/**
 * Render actions column
 */
export function renderActionsColumn(
  _value: unknown,
  item: Gateway,
  t: (key: string) => string,
  onEdit: (gateway: Gateway) => void,
  onDuplicate: (gateway: Gateway) => void,
  onDelete: (gateway: Gateway) => void,
  onReload?: (gateway: Gateway) => void,
  onStop?: (gateway: Gateway) => void,
  isStopped?: boolean,
  onStart?: (gateway: Gateway) => void
) {
  return (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')}>
        <button
          onClick={() => onEdit(item)}
          className={ACTION_BUTTON_STYLES.edit}
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')}>
        <button
          onClick={() => onDuplicate(item)}
          className={ACTION_BUTTON_STYLES.duplicate}
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      {onReload && (
        <SimpleTooltip content={t('gateways.reload')}>
          <button
            onClick={() => onReload(item)}
            className={ACTION_BUTTON_STYLES.reload}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      )}
      {isStopped && onStart ? (
        <SimpleTooltip content={t('gateways.start') || 'Start Trunk/Gateway'}>
          <button
            onClick={() => onStart(item)}
            className={ACTION_BUTTON_STYLES.start}
          >
            <Play className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      ) : onStop ? (
        <SimpleTooltip content={t('gateways.stop')}>
          <button
            onClick={() => onStop(item)}
            className={ACTION_BUTTON_STYLES.stop}
          >
            <Square className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      ) : null}
      <SimpleTooltip content={t('common.delete')}>
        <button
          onClick={() => onDelete(item)}
          className={ACTION_BUTTON_STYLES.delete}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  );
}

